import { c as _c } from "react/compiler-runtime";
import { postHostedUserDocumentsByIdProcessMutation } from '@onefootprint/fetch-hosted';
import { useRequestError } from '@onefootprint/request';
import type { DocumentResponse } from '@onefootprint/request-types';
import { IdDocImageTypes } from '@onefootprint/types';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getLogger, trackAction } from '@/idv/utils';
import IdDocAnimation from '../../../components/id-doc-animation';
import Loading from '../../../components/loading';
import RetryLimitExceeded from '../../../components/retry-limit-exceeded';
import Success from '../../../components/success';
import { SLOW_CONNECTION_MESSAGE_TIMEOUT } from '../../../constants';
import useSubmitDoc from '../../../hooks/use-submit-doc';
import { IdDocImageUploadError } from '../../../types';
import { bytesToMegabytes } from '../../../utils/capture';
import getBackendErrorMessage from '../../../utils/get-backend-error-message';
import getRejectionCodes from '../../../utils/get-rejection-codes';
import { useNonIdDocMachine } from '../../components/machine-provider';
const {
  logWarn,
  logInfo
} = getLogger({
  location: 'non-id-doc-mobile-processing'
});
const MobileProcessing = () => {
  const $ = _c(54);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      keyPrefix: "document-flow.non-id-doc.pages.desktop-processing"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    t
  } = useTranslation("idv", t0);
  const {
    getErrorMessage,
    getErrorCode
  } = useRequestError();
  const [state, send] = useNonIdDocMachine();
  const submitDocMutation = useSubmitDoc();
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = postHostedUserDocumentsByIdProcessMutation();
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const processDocMutation = useMutation(t1);
  const [mode, setMode] = useState("loading");
  const [showSlowConnectionMessage, setShowSlowConnectionMessage] = useState(false);
  const [retryLimitExceeded, setRetryLimitExceeded] = useState(false);
  const [isMissingRequirements, setIsMissingRequirements] = useState(false);
  const [step, setStep] = useState("upload");
  const slowConnectionTimer = useRef(null);
  const {
    document,
    authToken,
    id,
    requirement
  } = state.context;
  const {
    kind: documentRequestKind
  } = requirement.config;
  let t2;
  if ($[2] !== documentRequestKind) {
    t2 = () => {
      trackAction(`document-processing-${documentRequestKind}:started`);
    };
    $[2] = documentRequestKind;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== documentRequestKind || $[6] !== id || $[7] !== send) {
    t4 = data => {
      const {
        errorDetails,
        nextSideToCollect,
        isRetryLimitExceeded
      } = data;
      const rejectionCodes = getRejectionCodes(data);
      const rejectionContext = {
        rejectionCodes,
        docKind: documentRequestKind
      };
      if (isRetryLimitExceeded) {
        logWarn(`Image upload retry limit exceeded. doc: ${documentRequestKind}, doc id: ${id}`, undefined, rejectionContext);
        trackAction(`document-processing-${documentRequestKind}:retry-limit-exceeded`, rejectionContext);
        setRetryLimitExceeded(true);
        return;
      }
      if (nextSideToCollect === IdDocImageTypes.front) {
        logWarn(`Document rejected. doc: ${documentRequestKind}, codes: ${rejectionCodes}`, undefined, rejectionContext);
        trackAction(`document-processing-${documentRequestKind}:rejected`, rejectionContext);
        send({
          type: "processingErrored",
          payload: {
            errors: errorDetails.map(_temp)
          }
        });
        return;
      }
      if (nextSideToCollect) {
        const unexpectedContext = {
          ...rejectionContext,
          nextSide: nextSideToCollect
        };
        logWarn(`Unexpected next side to collect. Side: ${nextSideToCollect}`, undefined, unexpectedContext);
        trackAction(`document-processing-${documentRequestKind}:unexpected-next-side`, unexpectedContext);
        return;
      }
      setMode("success");
      trackAction(`document-processing-${documentRequestKind}:completed`, rejectionContext);
    };
    $[5] = documentRequestKind;
    $[6] = id;
    $[7] = send;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleProcessDocSuccess = t4;
  let t5;
  if ($[9] !== send) {
    t5 = err_0 => {
      const backendMessage = getBackendErrorMessage(err_0);
      send({
        type: "processingErrored",
        payload: {
          errors: [backendMessage ? {
            errorType: "custom_error",
            errorInfo: backendMessage
          } : {
            errorType: IdDocImageUploadError.unknownUploadError
          }]
        }
      });
    };
    $[9] = send;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleError = t5;
  let t6;
  if ($[11] !== getErrorMessage || $[12] !== handleError || $[13] !== id) {
    t6 = err_1 => {
      handleError(err_1);
      logWarn(`Error while processing image ${id}: ${getErrorMessage(err_1)}`, err_1);
    };
    $[11] = getErrorMessage;
    $[12] = handleError;
    $[13] = id;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const handleProcessDocError = t6;
  let t7;
  if ($[15] !== documentRequestKind || $[16] !== getErrorCode || $[17] !== getErrorMessage || $[18] !== handleError || $[19] !== id) {
    t7 = err_2 => {
      const errorCode = getErrorCode(err_2);
      const errorMessage = getErrorMessage(err_2);
      if (errorCode === "E109") {
        setMode("success");
        return;
      }
      logWarn(`Submit failed on phone flow. Document kind: ${documentRequestKind}, upload session id: ${id}. Error: ${errorMessage}`, err_2);
      handleError(err_2);
    };
    $[15] = documentRequestKind;
    $[16] = getErrorCode;
    $[17] = getErrorMessage;
    $[18] = handleError;
    $[19] = id;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleSubmitDocError = t7;
  let t8;
  if ($[21] !== authToken || $[22] !== handleProcessDocError || $[23] !== handleProcessDocSuccess || $[24] !== id || $[25] !== processDocMutation) {
    t8 = async () => {
      if (!id) {
        return;
      }
      setStep("analyze");
      ;
      try {
        const response = await processDocMutation.mutateAsync({
          path: {
            id
          },
          headers: {
            "X-Fp-Authorization": authToken
          }
        });
        handleProcessDocSuccess(response);
      } catch (t9) {
        const error = t9;
        handleProcessDocError(error);
      }
    };
    $[21] = authToken;
    $[22] = handleProcessDocError;
    $[23] = handleProcessDocSuccess;
    $[24] = id;
    $[25] = processDocMutation;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const handleSubmitDocSuccess = t8;
  let t9;
  if ($[27] !== authToken || $[28] !== document || $[29] !== handleSubmitDocError || $[30] !== handleSubmitDocSuccess || $[31] !== id || $[32] !== submitDocMutation) {
    t9 = () => {
      if (!document || !authToken || !id) {
        setIsMissingRequirements(true);
        const error_0 = `Mobile flow - id-doc image could not be processed due to missing requirements. Requirements - document: ${document ? "OK" : "undefined"}, auth token: ${authToken ? "OK" : "undefined"}, id: ${id ? "OK" : "undefined"}`;
        logWarn(error_0);
        return;
      }
      if (!slowConnectionTimer.current) {
        slowConnectionTimer.current = setTimeout(() => {
          if (submitDocMutation.isSuccess) {
            return;
          }
          setShowSlowConnectionMessage(true);
        }, SLOW_CONNECTION_MESSAGE_TIMEOUT);
      }
      const {
        imageFile,
        extraCompressed,
        captureKind
      } = document;
      logInfo(`Uploading image with size ${bytesToMegabytes(imageFile.size)} MB and type ${imageFile.type} in POST request`);
      submitDocMutation.mutate({
        authToken,
        image: imageFile,
        extraCompress: extraCompressed,
        side: IdDocImageTypes.front,
        id,
        meta: {
          manual: captureKind === "manual",
          isUpload: captureKind === "upload"
        }
      }, {
        onSuccess: handleSubmitDocSuccess,
        onError: handleSubmitDocError,
        onSettled: () => {
          if (slowConnectionTimer.current) {
            clearTimeout(slowConnectionTimer.current);
            setShowSlowConnectionMessage(false);
          }
        }
      });
    };
    $[27] = authToken;
    $[28] = document;
    $[29] = handleSubmitDocError;
    $[30] = handleSubmitDocSuccess;
    $[31] = id;
    $[32] = submitDocMutation;
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  useEffect(t9, t10);
  let t11;
  if ($[35] !== send) {
    t11 = () => {
      send({
        type: "processingSucceeded"
      });
    };
    $[35] = send;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const handleSuccess = t11;
  if (isMissingRequirements) {
    let t12;
    if ($[37] !== t) {
      t12 = t(_temp2);
      $[37] = t;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    let t13;
    if ($[39] !== t12) {
      t13 = <div className="text-label-1 text-error text-center">{t12}</div>;
      $[39] = t12;
      $[40] = t13;
    } else {
      t13 = $[40];
    }
    return t13;
  }
  let t12;
  if ($[41] !== send) {
    t12 = () => {
      send({
        type: "retryLimitExceeded"
      });
    };
    $[41] = send;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  const handleRetryLimitExceeded = t12;
  if (retryLimitExceeded) {
    let t13;
    if ($[43] !== handleRetryLimitExceeded) {
      t13 = <RetryLimitExceeded onRetryLimitExceeded={handleRetryLimitExceeded} />;
      $[43] = handleRetryLimitExceeded;
      $[44] = t13;
    } else {
      t13 = $[44];
    }
    return t13;
  }
  let t13;
  if ($[45] !== showSlowConnectionMessage || $[46] !== step) {
    t13 = <Loading step={step} showSlowConnectionMessage={showSlowConnectionMessage} />;
    $[45] = showSlowConnectionMessage;
    $[46] = step;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== handleSuccess) {
    t14 = <Success onComplete={handleSuccess} />;
    $[48] = handleSuccess;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== mode || $[51] !== t13 || $[52] !== t14) {
    t15 = <div className="flex flex-col items-center justify-center h-full min-h-[280px]"><IdDocAnimation loadingComponent={t13} successComponent={t14} mode={mode} hasNextSide={false} /></div>;
    $[50] = mode;
    $[51] = t13;
    $[52] = t14;
    $[53] = t15;
  } else {
    t15 = $[53];
  }
  return t15;
};
export default MobileProcessing;
function _temp(err) {
  return {
    errorType: err.code ?? "custom_error",
    errorInfo: err.message
  };
}
function _temp2($) {
  return $["missing-requirement-error"];
}